export interface VideoEmbedOptions {
  autoplay?: boolean
  mute?: boolean
  loop?: boolean
  controls?: boolean
  startTime?: number
}

export interface VideoEmbedInfo {
  provider: 'youtube' | 'vimeo'
  videoId: string
  embedUrl: string
  thumbnailUrl: string
}

const YOUTUBE_PATTERNS = [
  // youtube.com/watch?v=ID
  /(?:youtube\.com\/watch\?.*v=)([\w-]{11})/,
  // youtu.be/ID
  /(?:youtu\.be\/)([\w-]{11})/,
  // youtube.com/embed/ID
  /(?:youtube\.com\/embed\/)([\w-]{11})/,
  // youtube.com/shorts/ID
  /(?:youtube\.com\/shorts\/)([\w-]{11})/,
]

const VIMEO_PATTERNS = [
  // vimeo.com/ID
  /(?:vimeo\.com\/)(\d+)/,
  // player.vimeo.com/video/ID
  /(?:player\.vimeo\.com\/video\/)(\d+)/,
]

export function parseVideoUrl(url: string, options?: VideoEmbedOptions | null): VideoEmbedInfo | null {
  if (!url || typeof url !== 'string') return null

  const trimmed = url.trim()
  if (!trimmed) return null

  for (const pattern of YOUTUBE_PATTERNS) {
    const match = trimmed.match(pattern)
    if (match?.[1]) {
      const params = new URLSearchParams({
        rel: '0',
        modestbranding: '1',
        iv_load_policy: '3',
        color: 'white',
      })
      if (options?.autoplay) { params.set('autoplay', '1'); params.set('mute', '1') }
      if (options?.mute) params.set('mute', '1')
      if (options?.loop) { params.set('loop', '1'); params.set('playlist', match[1]) }
      if (options?.controls === false) params.set('controls', '0')
      if (options?.startTime && options.startTime > 0) params.set('start', String(options.startTime))
      return {
        provider: 'youtube',
        videoId: match[1],
        embedUrl: `https://www.youtube-nocookie.com/embed/${match[1]}?${params.toString()}`,
        thumbnailUrl: `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`,
      }
    }
  }

  for (const pattern of VIMEO_PATTERNS) {
    const match = trimmed.match(pattern)
    if (match?.[1]) {
      const params = new URLSearchParams()
      if (options?.autoplay) { params.set('autoplay', '1'); params.set('muted', '1') }
      if (options?.mute) params.set('muted', '1')
      if (options?.loop) params.set('loop', '1')
      if (options?.startTime && options.startTime > 0) params.set('#t', `${options.startTime}s`)
      const qs = params.toString()
      return {
        provider: 'vimeo',
        videoId: match[1],
        embedUrl: `https://player.vimeo.com/video/${match[1]}${qs ? `?${qs}` : ''}`,
        thumbnailUrl: '',
      }
    }
  }

  return null
}

export function isValidVideoUrl(url: string): boolean {
  return parseVideoUrl(url) !== null
}
